import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Alert,
    Divider,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    useTheme,
    useMediaQuery,
    alpha
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as PendingIcon,
    Download as DownloadIcon,
    Close as CloseIcon,
    Info as InfoIcon,
    Edit as SignIcon
} from '@mui/icons-material';
import CryptoJS from "crypto-js";
import { ApiDetails, KEY } from "../../dummyData";
import SignaturePad from './components/SignaturePad';
import { tenantSignAgreement } from './services/agreementSignatureApi';

/**
 * TenantAgreementView Component
 *
 * Allows tenants to:
 * 1. View their pending agreements
 * 2. See landlord signature status
 * 3. Sign agreements digitally
 * 4. Download signed agreements
 *
 * This component requires signing to proceed (unlike ID upload which is optional)
 */
const TenantAgreementView = ({ snackBar }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Get tenant details from localStorage
    const getTenantDetails = () => {
        try {
            const details = CryptoJS.AES.decrypt(
                localStorage.getItem("tenant_details"),
                KEY
            );
            const originalText = details.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText);
        } catch (error) {
            console.error("Error decrypting tenant details:", error);
            return null;
        }
    };

    const tenantDetails = getTenantDetails();

    // State management
    const [agreements, setAgreements] = useState([]);
    const [selectedAgreement, setSelectedAgreement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [viewAgreementModal, setViewAgreementModal] = useState(false);
    const [signatureModal, setSignatureModal] = useState(false);
    const [signingAgreement, setSigningAgreement] = useState(false);
    const [loadingAgreementContent, setLoadingAgreementContent] = useState(false);

    // Fetch agreements on mount
    useEffect(() => {
        if (tenantDetails) {
            fetchTenantAgreements();
        } else {
            showSnackbar("error", "Unable to load tenant details. Please log in again.");
            setLoading(false);
        }
    }, []);

    // Show snackbar helper
    const showSnackbar = (type, message) => {
        if (snackBar) {
            snackBar({
                text: message,
                type: type
            });
        }
    };

    // Fetch all agreements for this tenant
    const fetchTenantAgreements = async () => {
        setLoading(true);

        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/agreements/fetch", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": tenantDetails.access_token
                },
                body: JSON.stringify({
                    tenant_id: tenantDetails.tenant_id,
                    property_id: tenantDetails.property_id,
                    landlord_id: tenantDetails.landlord_id
                }),
            });

            if (response.status === 403) {
                localStorage.clear();
                window.location.href = "/";
                return;
            }

            const data = await response.json();

            if (response.status === 200 || response.status === 201) {
                let agreementsData = Array.isArray(data) ? data : (data.agreements || []);

                // Map backend structure to frontend structure
                agreementsData = agreementsData.map(agreement => ({
                    id: agreement.id,
                    tenant_id: agreement.tenant_id,
                    property_id: agreement.property_id,
                    template_name: agreement.agreement_title || "Tenancy Agreement",
                    tenant_name: agreement.tenant_name,
                    property_name: agreement.property_name,
                    status: agreement.status || "DRAFT",
                    created_at: agreement.generated_at || agreement.created_at,
                    landlord_signed: agreement.landlord_signed,
                    tenant_signed: agreement.tenant_signed,
                    fully_signed: agreement.fully_signed,
                    landlord_signed_at: agreement.landlord_signed_at,
                    tenant_signed_at: agreement.tenant_signed_at
                }));

                setAgreements(agreementsData);
            } else {
                showSnackbar("error", data.message || "Failed to load agreements.");
            }
        } catch (error) {
            console.error("Error fetching agreements:", error);
            showSnackbar("error", "Failed to connect to server. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch full agreement content
    const fetchAgreementContent = async (agreementId) => {
        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/agreements/public/view", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    agreement_id: agreementId,
                    tenant_id: tenantDetails.tenant_id
                }),
            });

            const data = await response.json();

            if (response.status === 200 && data.success) {
                // Backend returns agreement_content, not content or generated_content
                return data.agreement;
            } else {
                showSnackbar("error", data.message || "Failed to load agreement content.");
                return null;
            }
        } catch (error) {
            console.error("Error fetching agreement content:", error);
            showSnackbar("error", "Failed to load agreement content.");
            return null;
        }
    };

    // Process agreement content - replace placeholders if needed
    const processAgreementContent = (content, agreement) => {
        if (!content) return content;

        // Replace common placeholders with actual data
        let processedContent = content;

        // Replace unit number placeholder if present
        if (tenantDetails?.unit_number) {
            processedContent = processedContent.replace(/\{\{UNIT_NUMBER\}\}/g, tenantDetails.unit_number);
        }

        return processedContent;
    };

    // View agreement details
    const viewAgreement = async (agreement) => {
        setSelectedAgreement(agreement);
        setViewAgreementModal(true);
        setLoadingAgreementContent(true);

        const fullAgreement = await fetchAgreementContent(agreement.id);

        if (fullAgreement) {
            // Backend returns agreement_content (not content or generated_content)
            // Process content to replace placeholders
            const processedContent = processAgreementContent(fullAgreement.agreement_content, fullAgreement);

            setSelectedAgreement({
                ...agreement,
                agreement_content: processedContent,
                landlord_signature_data: fullAgreement.landlord_signature_data,
                tenant_signature_data: fullAgreement.tenant_signature_data,
                landlord_signed_at: fullAgreement.landlord_signed_at,
                tenant_signed_at: fullAgreement.tenant_signed_at
            });
        }

        setLoadingAgreementContent(false);
    };

    // Open signature modal
    const openSignatureModal = (agreement) => {
        if (agreement.tenant_signed) {
            showSnackbar("warning", "You have already signed this agreement.");
            return;
        }

        if (!agreement.landlord_signed) {
            showSnackbar("warning", "Please wait for the landlord to sign first.");
            return;
        }

        setSelectedAgreement(agreement);
        setSignatureModal(true);
    };

    // Handle signature save
    const handleSignatureSave = async (signatureData) => {
        if (!selectedAgreement) return;

        setSigningAgreement(true);

        const result = await tenantSignAgreement(
            selectedAgreement.id,
            tenantDetails.tenant_id,
            signatureData
        );

        if (result.success) {
            showSnackbar("success", result.data.message || "Agreement signed successfully!");

            // Close modals
            setSignatureModal(false);
            setViewAgreementModal(false);
            setSelectedAgreement(null);

            // Refresh agreements list
            fetchTenantAgreements();
        } else {
            showSnackbar("error", result.error);
        }

        setSigningAgreement(false);
    };

    // Close modals
    const handleCloseModals = () => {
        setViewAgreementModal(false);
        setSignatureModal(false);
        setSelectedAgreement(null);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get status color
    const getStatusColor = (status) => {
        const statusMap = {
            'DRAFT': 'default',
            'PENDING_SIGNATURE': 'warning',
            'SIGNED': 'success',
            'ACTIVE': 'success',
            'EXPIRED': 'error',
            'TERMINATED': 'error'
        };
        return statusMap[status] || 'default';
    };

    // Filter unsigned agreements
    const unsignedAgreements = agreements.filter(a => !a.tenant_signed);
    const signedAgreements = agreements.filter(a => a.tenant_signed);

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#398DD2' }}>
                    Your Tenancy Agreements
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Review and sign your tenancy agreements. Signing is required to proceed with your tenancy.
                </Typography>
            </Paper>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading agreements...</Typography>
                </Box>
            ) : agreements.length === 0 ? (
                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed #ccc' }}>
                    <InfoIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                        No Agreements Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Your landlord hasn't generated any agreements yet. Please contact them for assistance.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {/* Unsigned Agreements - Priority Display */}
                    {unsignedAgreements.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Pending Signature
                                </Typography>
                                <Chip
                                    label={`${unsignedAgreements.length} Unsigned`}
                                    color="warning"
                                    size="small"
                                />
                            </Box>

                            <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Action Required:</strong> You must sign the following agreement(s) to proceed with your tenancy.
                                </Typography>
                            </Alert>

                            <Grid container spacing={2}>
                                {unsignedAgreements.map((agreement) => (
                                    <Grid item xs={12} md={6} key={agreement.id}>
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                border: '2px solid',
                                                borderColor: '#ff9800',
                                                bgcolor: alpha('#ff9800', 0.05)
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                                                    {agreement.template_name}
                                                </Typography>
                                                <Chip
                                                    label={agreement.status}
                                                    color={getStatusColor(agreement.status)}
                                                    size="small"
                                                />
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Agreement ID
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {agreement.id}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Created
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatDate(agreement.created_at)}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {/* Signature Status */}
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                                    Signature Status
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Chip
                                                        label="Landlord"
                                                        size="small"
                                                        icon={agreement.landlord_signed ? <CheckCircleIcon /> : <PendingIcon />}
                                                        color={agreement.landlord_signed ? "success" : "default"}
                                                    />
                                                    <Chip
                                                        label="You (Tenant)"
                                                        size="small"
                                                        icon={agreement.tenant_signed ? <CheckCircleIcon /> : <PendingIcon />}
                                                        color={agreement.tenant_signed ? "success" : "warning"}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Action Buttons */}
                                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    onClick={() => viewAgreement(agreement)}
                                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                                >
                                                    View Details
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    startIcon={<SignIcon />}
                                                    onClick={() => openSignatureModal(agreement)}
                                                    disabled={!agreement.landlord_signed || agreement.tenant_signed}
                                                    sx={{
                                                        bgcolor: '#ff9800',
                                                        textTransform: 'none',
                                                        borderRadius: 2,
                                                        '&:hover': { bgcolor: '#f57c00' }
                                                    }}
                                                >
                                                    Sign Now
                                                </Button>
                                            </Box>

                                            {!agreement.landlord_signed && (
                                                <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon fontSize="small" />}>
                                                    <Typography variant="caption">
                                                        Waiting for landlord to sign first
                                                    </Typography>
                                                </Alert>
                                            )}
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}

                    {/* Signed Agreements */}
                    {signedAgreements.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Signed Agreements
                                </Typography>
                                <Chip
                                    label={`${signedAgreements.length} Signed`}
                                    color="success"
                                    size="small"
                                />
                            </Box>

                            <Grid container spacing={2}>
                                {signedAgreements.map((agreement) => (
                                    <Grid item xs={12} md={6} key={agreement.id}>
                                        <Paper
                                            elevation={1}
                                            sx={{
                                                p: 3,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: '#4caf50',
                                                bgcolor: alpha('#4caf50', 0.05)
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                                                        {agreement.template_name}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label="Signed"
                                                    color="success"
                                                    size="small"
                                                />
                                            </Box>

                                            <Divider sx={{ my: 2 }} />

                                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Signed On
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatDate(agreement.tenant_signed_at)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Agreement ID
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {agreement.id}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => viewAgreement(agreement)}
                                                sx={{ textTransform: 'none', borderRadius: 2 }}
                                            >
                                                View Agreement
                                            </Button>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </>
            )}

            {/* View Agreement Modal */}
            <Dialog
                open={viewAgreementModal}
                onClose={handleCloseModals}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Agreement Preview
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selectedAgreement && (
                            <Chip
                                label={selectedAgreement.status}
                                color={getStatusColor(selectedAgreement.status)}
                                size="small"
                            />
                        )}
                        <IconButton onClick={handleCloseModals} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Divider sx={{ mb: 3 }} />

                    {selectedAgreement && (
                        <>
                            {/* Agreement Metadata */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">Agreement ID</Typography>
                                        <Typography variant="body2" fontWeight={500} sx={{ fontFamily: 'monospace' }}>
                                            {selectedAgreement.id}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">Created</Typography>
                                        <Typography variant="body2" fontWeight={500}>
                                            {formatDate(selectedAgreement.created_at)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">Landlord Signature</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                label={selectedAgreement.landlord_signed ? "Signed" : "Pending"}
                                                color={selectedAgreement.landlord_signed ? "success" : "default"}
                                                size="small"
                                                icon={selectedAgreement.landlord_signed ? <CheckCircleIcon /> : <PendingIcon />}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">Your Signature</Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                label={selectedAgreement.tenant_signed ? "Signed" : "Pending"}
                                                color={selectedAgreement.tenant_signed ? "success" : "warning"}
                                                size="small"
                                                icon={selectedAgreement.tenant_signed ? <CheckCircleIcon /> : <PendingIcon />}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Agreement Content Preview */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    minHeight: '400px',
                                    maxHeight: '500px',
                                    overflowY: 'auto',
                                    bgcolor: 'white'
                                }}
                            >
                                {loadingAgreementContent ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body2" color="textSecondary">
                                            Loading agreement content...
                                        </Typography>
                                    </Box>
                                ) : selectedAgreement.agreement_content ? (
                                    <div dangerouslySetInnerHTML={{ __html: selectedAgreement.agreement_content }} />
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: 2 }}>
                                        <Typography color="textSecondary" align="center">
                                            No content available
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>

                            {/* Signatures Section */}
                            {selectedAgreement.landlord_signature_data || selectedAgreement.tenant_signature_data ? (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Signatures
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {selectedAgreement.landlord_signature_data && (
                                            <Grid item xs={12} sm={6}>
                                                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                                        Landlord Signature
                                                    </Typography>
                                                    <img
                                                        src={selectedAgreement.landlord_signature_data}
                                                        alt="Landlord Signature"
                                                        style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                                                    />
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                                                        Signed: {formatDate(selectedAgreement.landlord_signed_at)}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}
                                        {selectedAgreement.tenant_signature_data && (
                                            <Grid item xs={12} sm={6}>
                                                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                                        Your Signature
                                                    </Typography>
                                                    <img
                                                        src={selectedAgreement.tenant_signature_data}
                                                        alt="Tenant Signature"
                                                        style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                                                    />
                                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                                                        Signed: {formatDate(selectedAgreement.tenant_signed_at)}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            ) : null}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {selectedAgreement && !selectedAgreement.tenant_signed && selectedAgreement.landlord_signed && (
                        <Button
                            startIcon={<SignIcon />}
                            onClick={() => {
                                setViewAgreementModal(false);
                                openSignatureModal(selectedAgreement);
                            }}
                            variant="contained"
                            sx={{
                                bgcolor: '#ff9800',
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#f57c00' }
                            }}
                        >
                            Sign Agreement
                        </Button>
                    )}
                    <Button onClick={handleCloseModals} sx={{ textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Signature Modal */}
            <Dialog
                open={signatureModal}
                onClose={() => !signingAgreement && handleCloseModals()}
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown={signingAgreement}
            >
                <DialogTitle sx={{
                    color: '#398DD2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SignIcon />
                        Sign Your Agreement
                    </Box>
                    {!signingAgreement && (
                        <IconButton onClick={handleCloseModals} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Divider sx={{ mb: 3 }} />

                    {/* Agreement Info */}
                    {selectedAgreement && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                bgcolor: '#f5f5f5',
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                You are signing:
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                                {selectedAgreement.template_name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Agreement ID: {selectedAgreement.id}
                            </Typography>
                        </Paper>
                    )}

                    {/* Important Notice */}
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            By signing this agreement, you confirm that you have read, understood, and agree to all terms and conditions.
                        </Typography>
                    </Alert>

                    {/* Signature Pad */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <SignaturePad
                            onSave={handleSignatureSave}
                            onCancel={handleCloseModals}
                            signerName={tenantDetails?.first_name || "Tenant"}
                            loading={signingAgreement}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default TenantAgreementView;
